import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

import ENV from "@config/env";
import { downloadTemplate } from "@utils/bulkMeterUpload/downloadTemplate";
import { toastError, toastWarning } from "@utils/toast";

type TemplateDownloadButtonProps = {
  level1ResourceId: string;
  level1ResourceIdentifier?: string;
  className?: string;
  showArrow?: boolean;
};

export const TemplateDownloadButton = ({
  level1ResourceId,
  level1ResourceIdentifier,
  className = "btn-outline-primary text-sm rounded",
  showArrow = false,
}: TemplateDownloadButtonProps) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const outcome = await downloadTemplate({
        level1ResourceId,
        isSeqwater: ENV.CLIENT_ID === "seqwater",
        fileName: `bulk_meter_template_${level1ResourceIdentifier ?? level1ResourceId}.xlsx`,
        t,
      });
      if (outcome === "empty") {
        toastWarning(t("bulk_meter_upload.message.template_empty"));
      }
    } catch (error: any) {
      toastError(
        t("bulk_meter_upload.message.template_error", {
          error: error?.message,
        }),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const label = isDownloading
    ? t("common.loading")
    : t("bulk_meter_upload.download_template");

  return (
    <button
      type="button"
      className={className}
      onClick={handleDownload}
      disabled={isDownloading}
    >
      {showArrow ? (
        <>
          <span className="mr-2 text-left">{label}</span>
          <ArrowRightIcon className="w-4 h-4 shrink-0" />
        </>
      ) : (
        label
      )}
    </button>
  );
};
